import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Loading } from "./Layout";
import { SlidersHorizontal, X } from "lucide-react";

const inp = "border border-[#DFDAD0] px-2 py-1.5 text-sm outline-none focus:border-[#1C2530]";

function RepProfile({ rep, prof, industries, regions, onChange }) {
  const set = (k, v) => onChange({ ...prof, [k]: v });
  const toggle = (k, v) => { const cur = new Set(prof[k] || []); cur.has(v) ? cur.delete(v) : cur.add(v); set(k, [...cur]); };
  const Chips = ({ k, opts }) => (
    <div className="flex flex-wrap gap-1">
      {opts.map((o) => (
        <button key={o} onClick={() => toggle(k, o)} data-testid={`alloc-${k}-${rep.id}-${o}`}
          className={`font-mono text-[9px] uppercase px-1.5 py-0.5 border ${(prof[k] || []).includes(o) ? "bg-[#1C2530] text-[#F5F3EE] border-[#1C2530]" : "border-[#DFDAD0] text-[#1C2530]/60"}`}>{o}</button>
      ))}
      {opts.length === 0 && <span className="text-[10px] text-[#1C2530]/40">none configured</span>}
    </div>
  );
  return (
    <div className="border border-[#DFDAD0] p-3 space-y-2" data-testid={`alloc-profile-${rep.id}`}>
      <div className="text-sm font-medium text-[#1C2530]">{rep.name} <span className="font-mono text-[10px] text-[#1C2530]/50">· home {rep.region || "—"}</span></div>
      <div><div className="font-mono text-[9px] uppercase text-[#1C2530]/50 mb-1">Regions (blank = home region)</div><Chips k="regions" opts={regions} /></div>
      <div><div className="font-mono text-[9px] uppercase text-[#1C2530]/50 mb-1">Industries (blank = any)</div><Chips k="industries" opts={industries} /></div>
      <div className="flex items-center gap-2 text-xs">
        <span className="font-mono text-[9px] uppercase text-[#1C2530]/50">Deal value</span>
        <input type="number" placeholder="min" value={prof.min_deal_value ?? ""} onChange={(e) => set("min_deal_value", e.target.value === "" ? null : Number(e.target.value))} data-testid={`alloc-min-${rep.id}`} className={`${inp} w-24`} />
        <span>–</span>
        <input type="number" placeholder="max" value={prof.max_deal_value ?? ""} onChange={(e) => set("max_deal_value", e.target.value === "" ? null : Number(e.target.value))} data-testid={`alloc-max-${rep.id}`} className={`${inp} w-24`} />
        <label className="ml-auto flex items-center gap-1 cursor-pointer"><input type="checkbox" checked={prof.senior_ok !== false} onChange={(e) => set("senior_ok", e.target.checked)} data-testid={`alloc-senior-${rep.id}`} /> senior contacts OK</label>
      </div>
    </div>
  );
}

export default function AllocationRulesEditor({ onClose, onSaved, canEdit }) {
  const [r, setR] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/allocation/rules").then((x) => setR(x.data)).catch(() => toast.error("Could not load rules")); }, []);
  if (!r) return <div className="border border-[#DFDAD0] bg-white p-5"><Loading /></div>;
  const total = Object.values(r.weights).reduce((a, b) => a + Number(b || 0), 0);
  const save = async () => {
    setBusy(true);
    try { await api.put("/allocation/rules", { enabled: r.enabled, suggest_for: r.suggest_for, min_score: Number(r.min_score), weights: r.weights, rep_profiles: r.rep_profiles }); toast.success("Allocation rules saved"); onSaved?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); } finally { setBusy(false); }
  };
  return (
    <div className="border border-[#DFDAD0] bg-white p-5 space-y-5" data-testid="allocation-rules-editor">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60"><SlidersHorizontal className="w-3.5 h-3.5" /> Allocation rules · how suggestions are scored</div>
        <button onClick={onClose} data-testid="alloc-rules-close" className="text-[#1C2530]/50 hover:text-[#1C2530]"><X className="w-4 h-4" /></button>
      </div>
      <div className="flex flex-wrap items-center gap-4 text-sm">
        <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={!!r.enabled} disabled={!canEdit} onChange={(e) => setR({ ...r, enabled: e.target.checked })} data-testid="alloc-enabled" /> Suggestions on</label>
        <label className="flex items-center gap-2">Suggest for
          <select value={r.suggest_for} disabled={!canEdit} onChange={(e) => setR({ ...r, suggest_for: e.target.value })} data-testid="alloc-suggest-for" className={inp}>
            <option value="all">all unassigned leads</option><option value="event">event-triggered leads only</option>
          </select>
        </label>
        <label className="flex items-center gap-2">Min score to suggest <input type="number" min="0" max="100" value={r.min_score} disabled={!canEdit} onChange={(e) => setR({ ...r, min_score: e.target.value })} data-testid="alloc-min-score" className={`${inp} w-20`} /></label>
      </div>
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="font-mono text-[9px] uppercase text-[#1C2530]/50">Factor weights</div>
          <div className={`font-mono text-[10px] ${total === 100 ? "text-[#2F7A3E]" : "text-[#E2711D]"}`} data-testid="alloc-weight-total">total {total}{total !== 100 ? " (aim for 100)" : ""}</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {r.factors.map(([k, desc]) => (
            <label key={k} className="flex items-center gap-3 border border-[#DFDAD0] px-3 py-2">
              <div className="flex-1"><div className="text-sm text-[#1C2530] capitalize">{k.replace("_", " ")}</div><div className="text-[11px] text-[#1C2530]/50">{desc}</div></div>
              <input type="range" min="0" max="100" value={r.weights[k]} disabled={!canEdit} onChange={(e) => setR({ ...r, weights: { ...r.weights, [k]: Number(e.target.value) } })} data-testid={`alloc-weight-${k}`} className="w-28 accent-[#E50914]" />
              <span className="font-mono text-xs w-8 text-right">{r.weights[k]}</span>
            </label>
          ))}
        </div>
      </div>
      <div>
        <div className="font-mono text-[9px] uppercase text-[#1C2530]/50 mb-2">Rep profiles · what each rep is best placed to take</div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {r.reps.map((rep) => (
            <RepProfile key={rep.id} rep={rep} prof={(r.rep_profiles || {})[rep.id] || {}} industries={r.industries} regions={r.regions}
              onChange={(p) => canEdit && setR({ ...r, rep_profiles: { ...(r.rep_profiles || {}), [rep.id]: p } })} />
          ))}
          {r.reps.length === 0 && <div className="text-xs text-[#1C2530]/50 italic">No reps yet.</div>}
        </div>
      </div>
      {canEdit ? (
        <button onClick={save} disabled={busy} data-testid="alloc-rules-save" className="bg-[#1C2530] text-[#F5F3EE] px-4 py-2 text-sm hover:bg-[#2a3644] disabled:opacity-40">{busy ? "Saving…" : "Save rules"}</button>
      ) : <div className="text-xs text-[#1C2530]/50">Only an Admin can change allocation rules.</div>}
    </div>
  );
}
